import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Check, Landmark, Loader2, X } from 'lucide-react';
import { api } from '@/lib/api';
import { BankPicker } from '@/features/parent/wallet/ui';

const errText = (e: any) => e?.response?.data?.message ?? (e?.response?.data?.errors ? Object.values(e.response.data.errors).flat().join(' ') : 'Something went wrong.');

/** Add or change the account a staff member's salary is paid into: pick the bank, type the 10 digits, the bank tells us the name. */
export function StaffBankModal({ staff, onClose, onSaved }: { staff: { user_id: number; name: string; bank?: string | null }; onClose: () => void; onSaved: (msg: string) => void }) {
  const banksQ = useQuery({ queryKey: ['school-wallet-banks'], queryFn: () => api.get('/school/wallet/banks').then((r) => { const d = r.data?.data ?? r.data; return (Array.isArray(d) ? d : d?.banks ?? []) as { name: string; code: string }[]; }), staleTime: 3600_000 });
  const [code, setCode] = useState('');
  const [acct, setAcct] = useState('');
  const [name, setName] = useState('');
  const [manual, setManual] = useState(false);
  const [checking, setChecking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const bank = (banksQ.data ?? []).find((b) => b.code === code);

  useEffect(() => {
    setName(''); setManual(false); setErr('');
    if (!code || acct.length !== 10) return;
    let live = true;
    setChecking(true);
    api.post('/school/wallet/resolve-account', { bank_code: code, account_number: acct })
      .then((r) => { if (!live) return; const d = r.data?.data ?? r.data; const n = d?.account_name; if (n) setName(n); else setManual(true); })
      .catch(() => { if (live) setManual(true); })
      .finally(() => { if (live) setChecking(false); });
    return () => { live = false; };
  }, [code, acct]);

  const save = async () => {
    setBusy(true); setErr('');
    try { const r = await api.put(`/school/payroll/salaries/${staff.user_id}/bank`, { bank_code: code, bank_name: bank?.name, account_number: acct, account_name: name.trim() }); onSaved(r.data?.message ?? 'Bank details saved.'); }
    catch (e) { setErr(errText(e)); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-2xl shadow-2xl max-h-[92vh] flex flex-col">
        <div className="px-5 pt-5 pb-3 border-b border-slate-100 flex items-start gap-3">
          <span className="w-10 h-10 rounded-xl bg-[#0A2540]/5 text-[#0A2540] grid place-items-center shrink-0"><Landmark className="w-5 h-5" /></span>
          <div className="flex-1 min-w-0"><h3 className="font-bold text-lg text-slate-900 leading-tight">Salary account · {staff.name}</h3>
            <p className="text-xs text-slate-500">{staff.bank ? `Now: ${staff.bank}` : 'Their salary is paid into this account.'}</p></div>
          <button onClick={onClose} className="text-slate-400 p-1" aria-label="Close"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4 overflow-y-auto">
          <div><p className="text-sm font-semibold text-slate-700 mb-1">Bank</p><BankPicker banks={banksQ.data ?? []} loading={banksQ.isLoading} value={code} onChange={setCode} /></div>
          <label className="block"><span className="text-sm font-semibold text-slate-700">Account number</span>
            <input inputMode="numeric" maxLength={10} value={acct} onChange={(e) => setAcct(e.target.value.replace(/\D/g, ''))} placeholder="10 digits" className="mt-1 w-full border border-slate-300 rounded-xl px-3 py-3 text-base font-mono tracking-wider" /></label>
          {checking && <p className="text-sm text-slate-500 inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Checking the name with the bank…</p>}
          {name && !manual && <p className="text-sm rounded-xl bg-green-50 border border-green-200 px-3 py-2.5 text-green-900"><Check className="w-4 h-4 inline -mt-0.5 mr-1" /><b>{name}</b></p>}
          {manual && (
            <label className="block"><span className="text-sm font-semibold text-slate-700">Account name</span>
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="As written by the bank" className="mt-1 w-full border border-slate-300 rounded-xl px-3 py-3 text-sm" />
              <span className="text-[11px] text-amber-700">The bank could not confirm the name right now. Type it exactly as on the account.</span></label>
          )}
          {err && <p className="text-sm text-red-600 bg-red-50 rounded-xl px-3 py-2">{err}</p>}
        </div>
        <div className="px-5 py-3 border-t border-slate-100 flex gap-2">
          <button onClick={onClose} className="flex-1 py-3 rounded-xl border border-slate-300 text-sm font-bold">Cancel</button>
          <button onClick={save} disabled={busy || !code || acct.length !== 10 || name.trim().length < 3 || checking} className="flex-1 py-3 rounded-xl bg-[#0A2540] text-white text-sm font-bold disabled:opacity-40">{busy ? 'Saving…' : 'Save account'}</button>
        </div>
      </div>
    </div>
  );
}
